import React, { useState, useEffect, useCallback, useRef } from 'react';
import { placeOrder, isOrderOperationInProgress } from '../../../services/websocketApi/orders';
import OrderFormConfirmation from './OrderFormConfirmation';
import { validateOrderAmount, validatePrice, prepareOrderDetails, formatOrderParams } from './OrderFormUtils';
import './OrderFormStyles.css';

/**
 * OrderForm component for entering trade order details
 */
const OrderForm = ({ 
  market, 
  bid, 
  ask, 
  currentPrice,
  displayPrice,
  formatCurrency,
  getInstrumentDetails,
  isLoading
}) => {
  // Use ref to track if this is first load or market change
  const initialLoadRef = useRef(true);
  
  // Refs for interval timers
  const incrementTimerRef = useRef(null);
  const decrementTimerRef = useRef(null);
  const limitPriceIncrementTimerRef = useRef(null);
  const limitPriceDecrementTimerRef = useRef(null);
  
  // State for button pressing status
  const [isPressingAmountIncrement, setIsPressingAmountIncrement] = useState(false);
  const [isPressingAmountDecrement, setIsPressingAmountDecrement] = useState(false);
  const [isPressingLimitIncrement, setIsPressingLimitIncrement] = useState(false);
  const [isPressingLimitDecrement, setIsPressingLimitDecrement] = useState(false);
  
  // Side selection state
  const [side, setSide] = useState('sell');

  // Order type state
  const [orderType, setOrderType] = useState('market');
  
  // Order size state
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  
  // Price state for limit orders
  const [limitPrice, setLimitPrice] = useState('');
  
  // Stop Loss / Take Profit state
  const [stopLossEnabled, setStopLossEnabled] = useState(false);
  const [stopLossPrice, setStopLossPrice] = useState('');
  const [takeProfitEnabled, setTakeProfitEnabled] = useState(false);
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  
  // UI state for collapsible sections
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Leverage selection
  const [leverage, setLeverage] = useState(1);
  
  // Order confirmation state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  
  // Instrument details
  const instrumentDetails = getInstrumentDetails;
  
  // Available leverage options 
  const leverageOptions = [1, 2, 5, 10, 20, 50, 100];

  // Extract contract size and order limits from instrument details
  const contractSize = instrumentDetails?.contract_Size ? 
    parseFloat(instrumentDetails.contract_Size) : 
    (market?.contract_Size ? parseFloat(market.contract_Size) : null);
  
  const minOrderSize = instrumentDetails?.min_Order_Size ? 
    parseFloat(instrumentDetails.min_Order_Size) : 
    (market?.min_Order_Size ? parseFloat(market.min_Order_Size) : null);
  
  const maxOrderSize = instrumentDetails?.max_Order_Size ? 
    parseFloat(instrumentDetails.max_Order_Size) : 
    (market?.max_Order_Size ? parseFloat(market.max_Order_Size) : null);

  // Determine pip size based on instrument details
  const pipDecimalPlaces = instrumentDetails?.pipPosition || 
                          instrumentDetails?.pipDecimals || 
                          instrumentDetails?.decimalPlaces || 5;
  
  const pipSize = Math.pow(10, -pipDecimalPlaces);

  // Check if we have valid contract size and order limits
  const hasOrderSizeLimits = minOrderSize !== null && maxOrderSize !== null;
  const hasContractSize = contractSize !== null;

  // Calculate display quantities for the UI
  const displayMinQuantity = hasOrderSizeLimits && hasContractSize ? 
    minOrderSize * contractSize : null;
  const displayMaxQuantity = hasOrderSizeLimits && hasContractSize ? 
    maxOrderSize * contractSize : null;

  // Size limit error state
  const [sizeLimitError, setSizeLimitError] = useState('');

  // Initialize with minimum amount when component loads or instrument changes
  useEffect(() => {
    // Only set minimum amount if amount is empty or invalid
    if (displayMinQuantity !== null && (!amount || amount === '' || isNaN(parseFloat(amount)))) {
      setAmount(displayMinQuantity.toString());
      setAmountError('');
    }
    
    // Initialize with current price for limit orders
    if (currentPrice) {
      if (!limitPrice || limitPrice === '' || isNaN(parseFloat(limitPrice))) {
        setLimitPrice(formatCurrency(currentPrice));
      }
      
      // Set default stop loss and take profit prices only if they're not set
      if (!stopLossPrice || stopLossPrice === '' || isNaN(parseFloat(stopLossPrice))) {
        setStopLossPrice(formatCurrency(currentPrice * 0.95));
      }
      
      if (!takeProfitPrice || takeProfitPrice === '' || isNaN(parseFloat(takeProfitPrice))) {
        setTakeProfitPrice(formatCurrency(currentPrice * 1.05));
      }
    }
  }, [market, instrumentDetails, displayMinQuantity, currentPrice, formatCurrency, amount, limitPrice, stopLossPrice, takeProfitPrice]);

  // Separate effect to set the initial leverage only when market first loads
  useEffect(() => {
    // Only set default leverage when component first mounts or market completely changes
    if (market) {
      if (initialLoadRef.current) {
        setLeverage(1);
        initialLoadRef.current = false;
      } else {
        // For subsequent market changes, preserve the leverage unless it's invalid
        if (!leverage || leverage < 1) {
          setLeverage(1);
        }
      }
    }
  }, [market, leverage]);

  // Log instrument details for debugging
  useEffect(() => {
      
    
    // Set error state if limits are missing
    if (!hasOrderSizeLimits) {
      setSizeLimitError('Order size limits not available for this instrument');
    } else if (!hasContractSize) {
      setSizeLimitError('Contract size not available for this instrument');
    } else {
      setSizeLimitError('');
    }
  }, [market, instrumentDetails, minOrderSize, maxOrderSize, contractSize, 
      displayMinQuantity, displayMaxQuantity, hasOrderSizeLimits, hasContractSize]);

  // Add debugging effect to track leverage changes
  useEffect(() => {
  }, [leverage]);

  // Handler for side tab change (buy/sell)
  const handleSideChange = (newSide) => {
    // Only update the side
    setSide(newSide);
    
    // When side changes and we're in limit order mode, don't update the price at all
    // This allows users to manually enter and maintain their price
  };

  // Handler for order type change
  const handleOrderTypeChange = (newType) => {
    setOrderType(newType);
    
    if (newType === 'market') {
      // For market orders, collapse the panels but don't disable them
      setExpandedSection(null);
    } else if (newType === 'limit') {
      // For limit orders, expand the Stop Loss section by default
      setExpandedSection('stopLoss');
      
      // Enable Stop Loss by default for limit orders
      setStopLossEnabled(true);
      
      // Initialize limit price with appropriate price based on side ONLY if not already set
      if (!limitPrice || limitPrice === '' || isNaN(parseFloat(limitPrice))) {
        const appropriatePrice = side === 'buy' ? ask : bid;
        if (appropriatePrice) {
          setLimitPrice(formatCurrency(appropriatePrice));
          
          // Also update SL/TP based on new price
          updateStopLossAndTakeProfitForPrice(appropriatePrice);
        }
      }
    }
  };

  // Get current appropriate price based on side
  const getCurrentSidePrice = useCallback(() => {
    if (side === 'buy') {
      return ask || currentPrice || 0;
    } else {
      return bid || currentPrice || 0;
    }
  }, [side, ask, bid, currentPrice]);

  // Helper to determine if price is valid
  const isPriceValid = (price) => {
    return price !== null && price !== undefined && !isNaN(price) && price > 0;
  };

  // Handler for amount input
  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    
    // Clear error initially
    setAmountError('');
    
    // Show size limit error if limits are not available
    if (!hasOrderSizeLimits || !hasContractSize) {
      setAmountError(sizeLimitError);
      return;
    }
    
    // Validate the amount
    const error = validateOrderAmount(value, displayMinQuantity, displayMaxQuantity);
    if (error) {
      setAmountError(error);
    }
  };

  // Handler for limit price input
  const handleLimitPriceChange = (e) => {
    const value = e.target.value;
    setLimitPrice(value);
    
    // Validate the input to ensure it's a valid number
    const priceValue = parseFloat(value);
    if (value && !isNaN(priceValue) && priceValue > 0) {
      // If valid price, update SL/TP if enabled
      updateStopLossAndTakeProfitForPrice(priceValue);
    }
  };
  
  // Helper to update SL/TP based on price
  const updateStopLossAndTakeProfitForPrice = useCallback((priceValue) => {
    // If side is buy, stop loss should be below limit price
    if (side === 'buy' && stopLossEnabled) {
      setStopLossPrice(formatCurrency(priceValue * 0.95));
    }
    
    // If side is sell, stop loss should be above limit price
    if (side === 'sell' && stopLossEnabled) {
      setStopLossPrice(formatCurrency(priceValue * 1.05));
    }
    
    // Adjust take profit accordingly
    if (side === 'buy' && takeProfitEnabled) {
      setTakeProfitPrice(formatCurrency(priceValue * 1.05));
    }
    
    if (side === 'sell' && takeProfitEnabled) {
      setTakeProfitPrice(formatCurrency(priceValue * 0.95));
    }
  }, [side, stopLossEnabled, takeProfitEnabled, formatCurrency]);

  // Helpers for quick quantity setting
  const setMinAmount = () => {
    if (!hasOrderSizeLimits || !hasContractSize) {
      setAmountError(sizeLimitError);
      return;
    }
    
    setAmount(displayMinQuantity.toString());
    setAmountError('');
  };
  
  const setMaxAmount = () => {
    if (!hasOrderSizeLimits || !hasContractSize) {
      setAmountError(sizeLimitError);
      return;
    }
    
    setAmount(displayMaxQuantity.toString());
    setAmountError('');
  };

  // Increment/decrement amount by minimum order size
  const incrementAmount = useCallback(() => {
    if (!hasOrderSizeLimits || !hasContractSize) {
      setAmountError(sizeLimitError);
      return;
    }
    
    setAmount(prevAmount => {
      const currentAmount = prevAmount ? parseFloat(prevAmount) : 0;
      const incrementStep = displayMinQuantity; // Use displayMinQuantity as the step
      let newAmount;
      
      if (isNaN(currentAmount) || currentAmount < displayMinQuantity) {
        newAmount = displayMinQuantity;
      } else {
        newAmount = Math.min(currentAmount + incrementStep, displayMaxQuantity);
      }
      return newAmount.toString();
    });
    
    setAmountError('');
  }, [hasOrderSizeLimits, hasContractSize, displayMinQuantity, displayMaxQuantity, sizeLimitError]);
  
  const decrementAmount = useCallback(() => {
    if (!hasOrderSizeLimits || !hasContractSize) {
      setAmountError(sizeLimitError);
      return;
    }
    
    setAmount(prevAmount => {
      const currentAmount = prevAmount ? parseFloat(prevAmount) : 0;
      const decrementStep = displayMinQuantity; // Use displayMinQuantity as the step
      let newAmount;

      if (isNaN(currentAmount) || currentAmount <= displayMinQuantity) {
        newAmount = displayMinQuantity;
      } else {
        // Ensure we don't go below the minimum when decrementing
        newAmount = Math.max(currentAmount - decrementStep, displayMinQuantity);
      }
      return newAmount.toString();
    });
    
    setAmountError('');
  }, [hasOrderSizeLimits, hasContractSize, displayMinQuantity, sizeLimitError]);

  // Toggle SL/TP and collapsible sections
  const toggleSection = (section) => {
    // If clicking on the section header of an already expanded section, collapse it
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      // Otherwise expand it
      setExpandedSection(section);
      
      // When expanding a section, automatically enable the corresponding feature
      if (section === 'stopLoss' && !stopLossEnabled) {
        setStopLossEnabled(true);
      } else if (section === 'takeProfit' && !takeProfitEnabled) {
        setTakeProfitEnabled(true);
      }
    }
  };

  // Create modified versions of the checkbox handlers that completely isolate the events
  const handleStopLossCheckboxClick = (e) => {
    // Completely prevent any event bubbling
    e.preventDefault();
    e.stopPropagation();
    
    // Toggle the checkbox state
    const newState = !stopLossEnabled;
    setStopLossEnabled(newState);
    
    // If enabling, expand the section
    if (newState) {
      setExpandedSection('stopLoss');
    } else if (expandedSection === 'stopLoss') {
      // If disabling and this section is expanded, collapse it
      setExpandedSection(null);
    }
  };
  
  const handleTakeProfitCheckboxClick = (e) => {
    // Completely prevent any event bubbling
    e.preventDefault();
    e.stopPropagation();
    
    // Toggle the checkbox state
    const newState = !takeProfitEnabled;
    setTakeProfitEnabled(newState);
    
    // If enabling, expand the section
    if (newState) {
      setExpandedSection('takeProfit');
    } else if (expandedSection === 'takeProfit') {
      // If disabling and this section is expanded, collapse it
      setExpandedSection(null);
    }
  };

  // Validate if order can be placed
  const canPlaceOrder = useCallback(() => {
    // Basic validation
    if (!market || !amount || amountError) {
      return false;
    }
    
    // For limit orders, validate price
    if (orderType === 'limit' && (!limitPrice || isNaN(parseFloat(limitPrice)))) {
      return false;
    }
    
    // Validate stop loss if enabled
    if (stopLossEnabled && (!stopLossPrice || isNaN(parseFloat(stopLossPrice)))) {
      return false;
    }
    
    // Validate take profit if enabled
    if (takeProfitEnabled && (!takeProfitPrice || isNaN(parseFloat(takeProfitPrice)))) {
      return false;
    }
    
    return true;
  }, [
    market, amount, amountError, orderType, limitPrice, 
    stopLossEnabled, stopLossPrice, takeProfitEnabled, takeProfitPrice
  ]);

  // Prepare for order confirmation
  const prepareOrderConfirmation = () => {
    if (!canPlaceOrder()) {
      return;
    }
    
    // Create order details for confirmation
    const details = prepareOrderDetails({
      market,
      orderType,
      side,
      limitPrice,
      amount,
      leverage,
      stopLossEnabled,
      stopLossPrice,
      takeProfitEnabled,
      takeProfitPrice,
      bid,
      ask,
      formatCurrency
    });
    
    // Store order details and show confirmation
    setOrderDetails(details);
    setShowConfirmation(true);
  };

  // Cancel confirmation
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setIsConfirming(false);
  };

  // Confirm and place order
  const handleConfirmOrder = async () => {
    if (!orderDetails || isConfirming) {
      return;
    }
    
    setIsConfirming(true);
    
    try {
      console.log('Placing order with details:', orderDetails);
      
      // Format order parameters for API
      const orderParams = formatOrderParams(orderDetails, contractSize);
      
      // Place order via API
      const result = await placeOrder(orderParams);
      console.log('Order placed successfully:', result);
      
      // Reset form on success
      setShowConfirmation(false);
      setAmount(displayMinQuantity ? displayMinQuantity.toString() : '');
      setLimitPrice(formatCurrency(currentPrice));
      
      // Show success state briefly
      // TODO: Add success notification
    } catch (error) {
      console.error('Error placing order:', error);
      // TODO: Add error notification
    } finally {
      setIsConfirming(false);
    }
  };

  // Increment/decrement limit price by pipette
  const incrementLimitPrice = useCallback(() => {
    setLimitPrice(prevLimitPrice => {
      let newPrice;
      const currentAppropriatePrice = getCurrentSidePrice();

      if (!prevLimitPrice || isNaN(parseFloat(prevLimitPrice))) {
        newPrice = currentAppropriatePrice;
      } else {
        const currentLimitPrice = parseFloat(prevLimitPrice);
        newPrice = currentLimitPrice + pipSize; // Use pipSize for increment
      }
      
      const formattedNewPrice = formatCurrency(newPrice);
      updateStopLossAndTakeProfitForPrice(newPrice);
      return formattedNewPrice;
    });
  }, [pipSize, formatCurrency, getCurrentSidePrice, updateStopLossAndTakeProfitForPrice]);
  
  const decrementLimitPrice = useCallback(() => {
    setLimitPrice(prevLimitPrice => {
      let newPrice;
      const currentAppropriatePrice = getCurrentSidePrice();

      if (!prevLimitPrice || isNaN(parseFloat(prevLimitPrice))) {
        newPrice = currentAppropriatePrice;
      } else {
        const currentLimitPrice = parseFloat(prevLimitPrice);
        // Decrement by pip size, ensuring it stays positive
        newPrice = Math.max(currentLimitPrice - pipSize, pipSize);
      }
      
      const formattedNewPrice = formatCurrency(newPrice);
      updateStopLossAndTakeProfitForPrice(newPrice);
      return formattedNewPrice;
    });
  }, [pipSize, formatCurrency, getCurrentSidePrice, updateStopLossAndTakeProfitForPrice]);

  // Clean up any active timers when component unmounts
  // Removed cleanup logic here as useEffect will handle intervals

  // --- Start/Stop Handlers (using state) ---

  const handleStartAmountIncrement = () => {
    incrementAmount(); // First increment
    setIsPressingAmountIncrement(true);
  };

  const handleStopAmountIncrement = () => {
    setIsPressingAmountIncrement(false);
  };

  const handleStartAmountDecrement = () => {
    decrementAmount(); // First decrement
    setIsPressingAmountDecrement(true);
  };

  const handleStopAmountDecrement = () => {
    setIsPressingAmountDecrement(false);
  };

  const handleStartLimitIncrement = () => {
    incrementLimitPrice(); // First increment
    setIsPressingLimitIncrement(true);
  };

  const handleStopLimitIncrement = () => {
    setIsPressingLimitIncrement(false);
  };

  const handleStartLimitDecrement = () => {
    decrementLimitPrice(); // First decrement
    setIsPressingLimitDecrement(true);
  };

  const handleStopLimitDecrement = () => {
    setIsPressingLimitDecrement(false);
  };

  // --- useEffect Hooks for Intervals ---

  // Effect for Amount Increment
  useEffect(() => {
    let intervalId = null;
    if (isPressingAmountIncrement) {
      intervalId = setInterval(() => {
        incrementAmount();
      }, 250); // Slower interval (500ms = 2 times per second)
    }

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPressingAmountIncrement, incrementAmount]);

  // Effect for Amount Decrement
  useEffect(() => {
    let intervalId = null;
    if (isPressingAmountDecrement) {
      intervalId = setInterval(() => {
        decrementAmount();
      }, 250); // Slower interval (500ms = 2 times per second)
    }

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPressingAmountDecrement, decrementAmount]);

  // Effect for Limit Price Increment
  useEffect(() => {
    let intervalId = null;
    if (isPressingLimitIncrement) {
      intervalId = setInterval(() => {
        incrementLimitPrice();
      }, 250); // Slower interval (500ms = 2 times per second)
    }

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPressingLimitIncrement, incrementLimitPrice]);

  // Effect for Limit Price Decrement
  useEffect(() => {
    let intervalId = null;
    if (isPressingLimitDecrement) {
      intervalId = setInterval(() => {
        decrementLimitPrice();
      }, 500); // Slower interval (500ms = 2 times per second)
    }

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPressingLimitDecrement, decrementLimitPrice]);

  // Render the order form
  return (
    <div className="of-container">
      {/* Buy/Sell Tabs */}
      <div className="of-tabs">
        <button 
          className={`of-tab of-tab-sell ${side === 'sell' ? 'of-tab-active' : ''}`}
          onClick={() => handleSideChange('sell')}
        >
          SELL
        </button>
        <button 
          className={`of-tab of-tab-buy ${side === 'buy' ? 'of-tab-active' : ''}`}
          onClick={() => handleSideChange('buy')}
        >
          BUY
        </button>
      </div>

      {/* Order Type Selection */}
      <div className="of-order-types">
        <button 
          className={`of-order-type ${orderType === 'market' ? 'of-order-type-active' : ''}`}
          onClick={() => handleOrderTypeChange('market')}
        >
          Market
        </button>
        <button 
          className={`of-order-type ${orderType === 'limit' ? 'of-order-type-active' : ''}`}
          onClick={() => handleOrderTypeChange('limit')}
        >
          Limit
        </button>
      </div>

      {/* Quantity Input */}
      <div className="of-form-group">
        <label className="of-form-label">Quantity</label>
        <div className="of-input-group">
          <button 
            className="of-input-addon of-button-decrement"
            onMouseDown={handleStartAmountDecrement} 
            onMouseUp={handleStopAmountDecrement}
            onMouseLeave={handleStopAmountDecrement}
            onTouchStart={(e) => { 
              e.preventDefault(); 
              e.stopPropagation();
              handleStartAmountDecrement(); 
            }}
            onTouchEnd={handleStopAmountDecrement}
            onTouchCancel={handleStopAmountDecrement}
            disabled={!hasOrderSizeLimits}
            type="button"
          >
            -
          </button>
          <input
            type="number"
            className="of-input"
            value={amount}
            onChange={handleAmountChange}
            placeholder="Enter amount"
            disabled={!hasOrderSizeLimits}
            step="any"
          />
          <button 
            className="of-input-addon of-button-increment"
            onMouseDown={handleStartAmountIncrement} 
            onMouseUp={handleStopAmountIncrement}
            onMouseLeave={handleStopAmountIncrement}
            onTouchStart={(e) => { 
              e.preventDefault(); 
              e.stopPropagation();
              handleStartAmountIncrement(); 
            }}
            onTouchEnd={handleStopAmountIncrement}
            onTouchCancel={handleStopAmountIncrement}
            disabled={!hasOrderSizeLimits}
            type="button"
          >
            +
          </button>
          <button 
            className="of-input-addon of-button-max"
            onClick={setMaxAmount}
            disabled={!hasOrderSizeLimits}
            type="button"
          >
            MAX
          </button>
        </div>
        {amountError && <div className="of-error">{amountError}</div>}
        
        {hasOrderSizeLimits && (
          <div className="of-size-limits">
            <span className="of-size-limit" onClick={setMinAmount}>
              Min: {displayMinQuantity}
            </span>
            <span className="of-size-limit" onClick={setMaxAmount}>
              Max: {displayMaxQuantity}
            </span>
          </div>
        )}
      </div>

      {/* Price Input for Limit Orders */}
      {orderType === 'limit' && (
        <div className="of-form-group">
          <label className="of-form-label">Limit Price</label>
          <div className="of-input-group">
            <button 
              className="of-input-addon of-button-decrement"
              onMouseDown={handleStartLimitDecrement} 
              onMouseUp={handleStopLimitDecrement}
              onMouseLeave={handleStopLimitDecrement}
              onTouchStart={(e) => { 
                e.preventDefault(); 
                e.stopPropagation();
                handleStartLimitDecrement(); 
              }}
              onTouchEnd={handleStopLimitDecrement}
              onTouchCancel={handleStopLimitDecrement}
              type="button"
            >
              -
            </button>
            <input
              type="number"
              className="of-input"
              value={limitPrice}
              onChange={handleLimitPriceChange}
              placeholder={`Current: ${formatCurrency(getCurrentSidePrice())}`}
              step="any"
            />
            <button 
              className="of-input-addon of-button-increment"
              onMouseDown={handleStartLimitIncrement} 
              onMouseUp={handleStopLimitIncrement}
              onMouseLeave={handleStopLimitIncrement}
              onTouchStart={(e) => { 
                e.preventDefault(); 
                e.stopPropagation();
                handleStartLimitIncrement(); 
              }}
              onTouchEnd={handleStopLimitIncrement} 
              onTouchCancel={handleStopLimitIncrement}
              type="button"
            >
              +
            </button>
          </div>
          <div className="of-price-info">
            <div className="of-current-price">
              Current {side === 'buy' ? 'Ask' : 'Bid'}: {formatCurrency(getCurrentSidePrice())}
            </div>
            <div>
              {side === 'buy' ? 
                <span>Buy limit: executes when price falls below limit</span> : 
                <span>Sell limit: executes when price rises above limit</span>
              }
            </div>
          </div>
        </div>
      )}

      {/* Leverage Selection */}
      <div className="of-form-group">
        <label className="of-form-label" htmlFor="leverage-select">Leverage</label>
        <div className="of-input-group">
          <select 
            id="leverage-select"
            className="of-input of-leverage-select"
            value={leverage || 1} // Ensure there's always a value
            onChange={(e) => {
              const newLeverage = parseInt(e.target.value, 10);
              // Force the value to be a number in the valid range
              const validLeverage = isNaN(newLeverage) ? 1 : newLeverage;
              setLeverage(validLeverage);
            }}
            aria-label="Select leverage"
          >
            {leverageOptions.map(option => (
              <option key={option} value={option}>
                {option}x Leverage
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stop Loss Section */}
      <div className={`of-collapsible ${expandedSection === 'stopLoss' ? 'of-collapsible-expanded' : ''}`}>
        <div 
          className="of-collapsible-header"
          onClick={() => toggleSection('stopLoss')}
        >
          <div 
            className="of-checkbox-group" 
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="of-checkbox-wrapper"
              onClick={handleStopLossCheckboxClick}
            >
              <input
                type="checkbox"
                className="of-checkbox"
                checked={stopLossEnabled}
                onChange={(e) => e.stopPropagation()} // Just stop propagation, actual toggle handled by wrapper
                id="stop-loss-toggle"
              />
              <label 
                className="of-checkbox-label" 
                htmlFor="stop-loss-toggle"
                onClick={(e) => e.stopPropagation()}
              >
                Stop Loss
              </label>
            </div>
          </div>
          <span>{expandedSection === 'stopLoss' ? '▲' : '▼'}</span>
        </div>
        
        {expandedSection === 'stopLoss' && (
          <div className="of-collapsible-content">
            <div className="of-form-group">
              <label className="of-form-label">Stop Loss Price</label>
              <div className="of-input-group">
                <input
                  type="number"
                  className="of-input"
                  value={stopLossPrice}
                  onChange={(e) => {
                    setStopLossPrice(e.target.value);
                  }}
                  placeholder="Stop Loss Price"
                  disabled={!stopLossEnabled}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Take Profit Section */}
      <div className={`of-collapsible ${expandedSection === 'takeProfit' ? 'of-collapsible-expanded' : ''}`}>
        <div 
          className="of-collapsible-header"
          onClick={() => toggleSection('takeProfit')}
        >
          <div 
            className="of-checkbox-group" 
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="of-checkbox-wrapper"
              onClick={handleTakeProfitCheckboxClick}
            >
              <input
                type="checkbox"
                className="of-checkbox"
                checked={takeProfitEnabled}
                onChange={(e) => e.stopPropagation()} // Just stop propagation, actual toggle handled by wrapper
                id="take-profit-toggle"
              />
              <label 
                className="of-checkbox-label" 
                htmlFor="take-profit-toggle"
                onClick={(e) => e.stopPropagation()}
              >
                Take Profit
              </label>
            </div>
          </div>
          <span>{expandedSection === 'takeProfit' ? '▲' : '▼'}</span>
        </div>
        
        {expandedSection === 'takeProfit' && (
          <div className="of-collapsible-content">
            <div className="of-form-group">
              <label className="of-form-label">Take Profit Price</label>
              <div className="of-input-group">
                <input
                  type="number"
                  className="of-input"
                  value={takeProfitPrice}
                  onChange={(e) => {
                    setTakeProfitPrice(e.target.value);
                  }}
                  placeholder="Take Profit Price"
                  disabled={!takeProfitEnabled}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Button */}
      <div className="of-actions">
        <button
          className={`of-button ${side === 'sell' ? 'of-button-sell' : 'of-button-buy'}`}
          onClick={prepareOrderConfirmation}
          disabled={!canPlaceOrder() || isLoading}
        >
          {isLoading ? 'PROCESSING...' : `${side === 'sell' ? 'SELL' : 'BUY'} ${orderType === 'limit' ? 'LIMIT' : ''}`}
        </button>
      </div>

      {/* Order Confirmation Modal */}
      <OrderFormConfirmation
        orderDetails={orderDetails}
        showConfirmation={showConfirmation}
        isConfirming={isConfirming}
        onCancel={handleCancelConfirmation}
        onConfirm={handleConfirmOrder}
      />
    </div>
  );
};

export default OrderForm; 